import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  Chip,
  CardMedia,
  Container,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getPlane } from "../../../Services/plansApi";
import { API_BASE_URL_images } from "../../../Services/apiConfig";
import { useNavigate } from "react-router-dom";

const getImageUrl = (path) => {
  if (!path) return null;
  if (typeof path !== "string") return null;
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  const base = (API_BASE_URL_images || "").replace(/\/$/, "");
  const p = path.replace(/^\/+/, "");
  return base ? `${base}/${p}` : path;
};

const PlanCard = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPlanId, setExpandedPlanId] = useState(null);
  const navigate = useNavigate();

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const result = await getPlane();

      // استخراج مصفوفة الباقات من أي شكل للـ response:
      // - result.data.data.palnes  (كما يظهر في سجلك)
      // - result.data.palnes       (fallback محتمل)
      const plansArray =
        result?.data?.data?.palnes || result?.data?.palnes || [];

      // احتفظ فقط بالباكات النشطة (status === 1)
      const activePlans = Array.isArray(plansArray)
        ? plansArray.filter((p) => Number(p.status) === 1)
        : [];

      // فرز التصاعدي حسب plane_order (ابدأ من 1). العناصر بدون plane_order تذهب للنهاية.
      activePlans.sort((a, b) => {
        const A = a?.plane_order != null ? Number(a.plane_order) : Number.MAX_SAFE_INTEGER;
        const B = b?.plane_order != null ? Number(b.plane_order) : Number.MAX_SAFE_INTEGER;
        return A - B;
      });

      setPlans(activePlans);
      console.log("Fetched active plans count:", activePlans.length);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleSelect = (plan) => {
    // Navigate to signup/create-account page and pass selected plan info
    navigate("/signup", { state: { planId: plan?.id, planName: plan?.plane_name } });
  };

  if (loading) {
    return (
      <Box
        sx={{
          py: 10,
          textAlign: "center",
          background: "linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)",
        }}
      >
        <Typography variant="h6" sx={{ color: "primary.main" }}>
          جاري تحميل الباقات...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        py: 10,
        background: "linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            "radial-gradient(circle, rgba(102, 126, 234, 0.03) 1px, transparent 1px)",
          backgroundSize: "30px 30px",
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h3"
            gutterBottom
            fontWeight={800}
            sx={{
              background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              mb: 2,
            }}
          >
            اختر الباقة المناسبة لك
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: "text.secondary", maxWidth: 600, mx: "auto" }}
          >
            باقات مرنة ومتنوعة تلبي احتياجات مطعمك مع أفضل الأسعار 🚀
          </Typography>
        </Box>

        <Grid container spacing={4} justifyContent="center">
          {plans.map((plan, index) => (
            <Grid item xs={12} sm={6} lg={4} key={plan.id}>
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 4,
                  position: "relative",
                  overflow: "visible",
                  background: "white",
                  boxShadow:
                    index === 1
                      ? "0 20px 60px rgba(243, 111, 33, 0.25)"
                      : "0 8px 24px rgba(0,0,0,0.08)",
                  border: index === 1 ? "3px solid #f36f21" : "1px solid #e0e0e0",
                  transform: index === 1 ? "scale(1.05)" : "scale(1)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: index === 1 ? "scale(1.08)" : "scale(1.05)",
                    boxShadow: "0 20px 60px rgba(243, 111, 33, 0.3)",
                  },
                  "&::before":
                    index === 1
                      ? {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: "5px",
                          background: "linear-gradient(90deg, #f36f21 0%, #e65100 100%)",
                        }
                      : {},
                }}
              >
                {index === 1 && (
                  <Chip
                    label="الأكثر شيوعاً ⭐"
                    sx={{
                      position: "absolute",
                      top: -15,
                      right: 20,
                      background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                      color: "white",
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      px: 2,
                      py: 2.5,
                      height: "auto",
                      boxShadow: "0 4px 12px rgba(243, 111, 33, 0.4)",
                      zIndex: 2,
                    }}
                  />
                )}

                {plan.image && (
                  <CardMedia
                    component="img"
                    height="180"
                    image={getImageUrl(plan.image)}
                    alt={plan.plane_name || "plan image"}
                    sx={{
                      objectFit: "cover",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  />
                )}

                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    fontWeight={700}
                    sx={{ color: "text.primary", mb: 2 }}
                  >
                    {plan.plane_name}
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    {(() => {
                      const original = Number(plan.price) || 0;
                      const discount = Number(plan.descount) || 0;
                      const after = Math.round((original * (1 - discount / 100)) * 100) / 100;
                      return (
                        <Box sx={{ mb: 0, textAlign: "center" }}>
                          {discount > 0 ? (
                            <>
                              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "baseline", gap: 1 }}>
                                <Typography
                                  variant="h2"
                                  sx={{
                                    fontWeight: 900,
                                    background: "linear-gradient(90deg, #f36f21 0%, #e65100 100%)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    fontSize: { xs: "1.9rem", sm: "2.4rem" },
                                  }}
                                >
                                  {after}
                                </Typography>
                                <Typography variant="h6" sx={{ color: "text.secondary" }}>
                                  جنيه
                                </Typography>
                              </Box>

                              <Box sx={{ mt: 1 }}>
                                <Typography variant="subtitle2" sx={{ color: "success.main", fontWeight: 800 }}>
                                  وفر {discount}%
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    color: "text.secondary",
                                    textDecoration: "line-through",
                                    mt: 0.5,
                                  }}
                                >
                                  السعر الأصلي: {original} جنيه
                                </Typography>
                              </Box>
                            </>
                          ) : (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "baseline", gap: 1 }}>
                              <Typography
                                variant="h3"
                                fontWeight={800}
                                sx={{
                                  background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                                  WebkitBackgroundClip: "text",
                                  WebkitTextFillColor: "transparent",
                                  backgroundClip: "text",
                                }}
                              >
                                {original}
                              </Typography>
                              <Typography variant="h6" sx={{ color: "text.secondary" }}>
                                جنيه
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      mb: 3,
                      minHeight: 45,
                      lineHeight: 1.6,
                    }}
                  >
                    {plan.desc || "مميزات رائعة تناسب احتياجات مطعمك"}
                  </Typography>

                  {plan.features?.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      {((expandedPlanId === plan.id ? plan.features : (plan.features || []).slice(0, 5)) || []).map((feature) => (
                         <Box
                           key={feature.id}
                           sx={{
                             display: "flex",
                             alignItems: "flex-start",
                             gap: 1.5,
                             mb: 2,
                           }}
                         >
                           <CheckCircleIcon
                             sx={{ color: "#f36f21", fontSize: 20, mt: 0.2 }}
                           />
                           <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.6 }}>
                             {feature.name}
                           </Typography>
                         </Box>
                       ))}
                      {plan.features?.length > 5 && (
                        <Button
                          onClick={() =>
                            setExpandedPlanId(expandedPlanId === plan.id ? null : plan.id)
                          }
                          variant="text"
                          sx={{
                            mt: 1,
                            color: "primary.main",
                            fontWeight: 600,
                            textTransform: "none",
                            px: 0,
                          }}
                        >
                          {expandedPlanId === plan.id
                            ? "عرض أقل"
                            : `+ ${plan.features.length - 5} مميزات إضافية`}
                        </Button>
                      )}
                    </Box>
                  )}

                  <Button
                    onClick={() => handleSelect(plan)}
                    variant={index === 1 ? "contained" : "outlined"}
                    size="large"
                    fullWidth
                    sx={{
                      py: 1.5,
                      fontWeight: 700,
                      fontSize: "1rem",
                      borderRadius: 3,
                      textTransform: "none",
                      ...(index === 1
                        ? {
                            background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                            boxShadow: "0 8px 20px rgba(243, 111, 33, 0.3)",
                            "&:hover": {
                              background: "linear-gradient(135deg, #e65100 0%, #d84315 100%)",
                              boxShadow: "0 12px 28px rgba(243, 111, 33, 0.4)",
                            },
                          }
                        : {
                            borderWidth: 2,
                            borderColor: "#f36f21",
                            color: "#f36f21",
                            "&:hover": {
                              borderWidth: 2,
                              borderColor: "#e65100",
                              background: "rgba(243, 111, 33, 0.05)",
                            },
                          }),
                    }}
                  >
                    اختر هذه الباقة
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default PlanCard;
